import React from 'react';
import { X } from 'lucide-react';
import { AppModal } from '../../../components/AppModal';
import { useDirtySnapshot } from '../../../hooks/useDiscardGuard';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_INPUT_CLASS,
} from '../../settings/utils/settingsSectionStyles';
import type {
  DocumentCategory,
  FuelProvider,
  ReminderType,
  RepairStatus,
  VehicleFuelCard,
  VehicleFuelTransaction,
  VehicleReminder,
  VehicleRepair,
} from '../types';
import {
  DOCUMENT_CATEGORY_OPTIONS,
  FUEL_PROVIDER_OPTIONS,
  REMINDER_TYPE_OPTIONS,
  REPAIR_STATUS_OPTIONS,
} from '../types';
import {
  REMINDER_TYPE_DEFAULTS,
  addMonthsToDate,
  centsToEuroInput,
  litersToInput,
  validateCompleteForm,
  validateReminderForm,
  validateRepairForm,
} from '../format';
import { DateField, Field, SelectField, TextField } from './FuhrparkFormFields';

function Footer({
  onClose,
  onSubmit,
  saving,
  label,
}: {
  onClose: () => void;
  onSubmit: () => void;
  saving: boolean;
  label: string;
}) {
  return (
    <div className="flex justify-end gap-2 border-t border-app-border px-4 py-3 md:px-5">
      <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={onClose} disabled={saving}>
        Abbrechen
      </button>
      <button type="button" className={SETTINGS_BTN_PRIMARY} onClick={onSubmit} disabled={saving}>
        {saving ? <LoadingSpinner size="sm" /> : null}
        {label}
      </button>
    </div>
  );
}

export function ReminderFormModal({
  open,
  initial,
  saving,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean;
  initial?: VehicleReminder | null;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => void;
}) {
  const [reminderType, setReminderType] = React.useState<ReminderType>('oil_change');
  const [label, setLabel] = React.useState('');
  const [dueOn, setDueOn] = React.useState('');
  const [dueMileage, setDueMileage] = React.useState('');
  const [lastDoneOn, setLastDoneOn] = React.useState('');
  const [lastDoneMileage, setLastDoneMileage] = React.useState('');
  const [intervalMonths, setIntervalMonths] = React.useState('');
  const [intervalKm, setIntervalKm] = React.useState('');
  const [dueSoonDays, setDueSoonDays] = React.useState('30');
  const [dueSoonKm, setDueSoonKm] = React.useState('');
  const [note, setNote] = React.useState('');
  const [localError, setLocalError] = React.useState<string | null>(null);
  const [formKey, setFormKey] = React.useState(0);

  React.useEffect(() => {
    if (!open) return;
    const defaults = REMINDER_TYPE_DEFAULTS[initial?.reminder_type ?? 'oil_change'];
    setReminderType(initial?.reminder_type ?? 'oil_change');
    setLabel(initial?.label ?? '');
    setDueOn(initial?.due_on ?? '');
    setDueMileage(initial?.due_mileage != null ? String(initial.due_mileage) : '');
    setLastDoneOn(initial?.last_done_on ?? '');
    setLastDoneMileage(initial?.last_done_mileage != null ? String(initial.last_done_mileage) : '');
    setIntervalMonths(
      initial
        ? initial.interval_months != null
          ? String(initial.interval_months)
          : ''
        : defaults.intervalMonths,
    );
    setIntervalKm(
      initial ? (initial.interval_km != null ? String(initial.interval_km) : '') : defaults.intervalKm,
    );
    setDueSoonDays(
      initial
        ? initial.due_soon_days != null
          ? String(initial.due_soon_days)
          : defaults.dueSoonDays
        : defaults.dueSoonDays,
    );
    setDueSoonKm(
      initial ? (initial.due_soon_km != null ? String(initial.due_soon_km) : '') : defaults.dueSoonKm,
    );
    setNote(initial?.interval_note ?? '');
    setLocalError(null);
    setFormKey((k) => k + 1);
  }, [open, initial]);
  const dirty = useDirtySnapshot(
    JSON.stringify([reminderType, label, dueOn, dueMileage, lastDoneOn, lastDoneMileage, intervalMonths, intervalKm, dueSoonDays, dueSoonKm, note.trim()]),
    formKey,
  );

  const applyTypeDefaults = (next: ReminderType) => {
    setReminderType(next);
    if (initial) return;
    const defaults = REMINDER_TYPE_DEFAULTS[next];
    setIntervalMonths(defaults.intervalMonths);
    setIntervalKm(defaults.intervalKm);
    setDueSoonDays(defaults.dueSoonDays);
    setDueSoonKm(defaults.dueSoonKm);
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      panelClassName="md:max-w-lg md:!rounded-xl md:overflow-hidden"
      overlayClassName="z-[70]"
      dirty={open && dirty}
      disableClose={saving}
    >
      {(requestClose) => (
        <div className="flex h-full flex-col overflow-hidden bg-app-surface md:rounded-xl">
          <div className="flex items-center justify-between gap-3 border-b border-app-border px-4 py-3">
            <h2 className="text-base font-semibold text-app-text">
              {initial ? 'Erinnerung bearbeiten' : 'Wartung / Erinnerung einrichten'}
            </h2>
            <button
              type="button"
              onClick={requestClose}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text"
              aria-label="Schließen"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            <SelectField
              label="Typ"
              value={reminderType}
              onChange={(v) => applyTypeDefaults(v as ReminderType)}
              options={REMINDER_TYPE_OPTIONS}
            />
            <TextField label="Bezeichnung (optional)" value={label} onChange={setLabel} />
            <div className="space-y-3 rounded-lg border border-app-border bg-app-surface-muted p-3">
              <div>
                <h3 className="text-sm font-semibold text-app-text">Wiederholung</h3>
                <p className="mt-0.5 text-xs text-app-text-subtle">
                  Nach jedem Abschluss wird die nächste Fälligkeit automatisch weitergerechnet.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <TextField
                  label="Alle wie viele Monate?"
                  value={intervalMonths}
                  onChange={setIntervalMonths}
                  placeholder="z. B. 12"
                  kind="int"
                />
                <TextField
                  label="Alle wie viele Kilometer?"
                  value={intervalKm}
                  onChange={setIntervalKm}
                  placeholder="z. B. 15.000"
                  kind="int"
                />
              </div>
              <p className="text-xs text-app-text-subtle">
                Sind beide Werte gesetzt, wird gewarnt, sobald Datum oder Kilometerstand erreicht sind.
              </p>
            </div>

            <div className="space-y-3 rounded-lg border border-app-border bg-app-surface p-3">
              <div>
                <h3 className="text-sm font-semibold text-app-text">Letzte Durchführung</h3>
                <p className="mt-0.5 text-xs text-app-text-subtle">
                  Optional als Ausgangspunkt für die erste automatische Berechnung.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <DateField label="Datum" value={lastDoneOn} onChange={setLastDoneOn} />
                <TextField label="Kilometerstand" value={lastDoneMileage} onChange={setLastDoneMileage} kind="int" />
              </div>
            </div>

            <div className="space-y-3 rounded-lg border border-app-border bg-app-surface p-3">
              <div>
                <h3 className="text-sm font-semibold text-app-text">Nächste Fälligkeit</h3>
                <p className="mt-0.5 text-xs text-app-text-subtle">
                  Kann unabhängig vom Intervall einmalig vorgegeben oder angepasst werden.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <DateField label="Fällig am" value={dueOn} onChange={setDueOn} />
                <TextField label="Fällig bei km" value={dueMileage} onChange={setDueMileage} kind="int" />
              </div>
            </div>

            <div className="space-y-3 rounded-lg border border-app-border bg-app-surface p-3">
              <div>
                <h3 className="text-sm font-semibold text-app-text">Vorwarnung</h3>
                <p className="mt-0.5 text-xs text-app-text-subtle">Wie früh die Erinnerung als „Bald fällig“ erscheint.</p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <TextField label="Tage vorher" value={dueSoonDays} onChange={setDueSoonDays} kind="int" />
                <TextField label="Kilometer vorher" value={dueSoonKm} onChange={setDueSoonKm} kind="int" />
              </div>
            </div>
            <TextField label="Notiz" value={note} onChange={setNote} />
            {localError || error ? <p className="text-sm text-rose-600">{localError || error}</p> : null}
          </div>
          <Footer
            onClose={onClose}
            saving={saving}
            label="Erinnerung speichern"
            onSubmit={() => {
              const validation = validateReminderForm({
                dueOn,
                dueMileage,
                intervalMonths,
                intervalKm,
              });
              if (validation) {
                setLocalError(validation);
                return;
              }
              const months = Number.parseInt(intervalMonths, 10);
              const intervalKmValue = Number.parseInt(intervalKm, 10);
              const lastKm = Number.parseInt(lastDoneMileage, 10);
              const computedDue =
                !dueOn && lastDoneOn && Number.isFinite(months) && months > 0
                  ? addMonthsToDate(lastDoneOn, months)
                  : dueOn;
              const computedDueMileage =
                !dueMileage && Number.isFinite(intervalKmValue) && intervalKmValue > 0 && Number.isFinite(lastKm)
                  ? String(lastKm + intervalKmValue)
                  : dueMileage;
              onSubmit({
                reminder_type: reminderType,
                label,
                interval_note: note,
                interval_months: intervalMonths || null,
                interval_km: intervalKm || null,
                due_soon_days: dueSoonDays || null,
                due_soon_km: dueSoonKm || null,
                last_done_on: lastDoneOn || null,
                last_done_mileage: lastDoneMileage || null,
                due_on: computedDue || null,
                due_mileage: computedDueMileage || null,
              });
            }}
          />
        </div>
      )}
    </AppModal>
  );
}

export function RepairFormModal({
  open,
  initial,
  saving,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean;
  initial?: VehicleRepair | null;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => void;
}) {
  const [title, setTitle] = React.useState('');
  const [workshop, setWorkshop] = React.useState('');
  const [repairDate, setRepairDate] = React.useState('');
  const [cost, setCost] = React.useState('');
  const [status, setStatus] = React.useState<RepairStatus>('open');
  const [localError, setLocalError] = React.useState<string | null>(null);
  const [formKey, setFormKey] = React.useState(0);

  React.useEffect(() => {
    if (!open) return;
    setTitle(initial?.title ?? '');
    setWorkshop(initial?.workshop ?? '');
    setRepairDate(initial?.repair_date ?? '');
    setCost(centsToEuroInput(initial?.cost_cents));
    setStatus(initial?.status ?? 'open');
    setLocalError(null);
    setFormKey((k) => k + 1);
  }, [open, initial]);
  const dirty = useDirtySnapshot(JSON.stringify([title, workshop, repairDate, cost, status]), formKey);

  return (
    <AppModal
      open={open}
      onClose={onClose}
      panelClassName="md:max-w-lg"
      overlayClassName="z-[70]"
      dirty={open && dirty}
      disableClose={saving}
    >
      <div className="flex h-full flex-col bg-app-surface">
        <div className="border-b border-app-border px-4 py-3">
          <h2 className="text-base font-semibold text-app-text">
            {initial ? 'Reparatur bearbeiten' : 'Reparatur erfassen'}
          </h2>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          <TextField label="Bezeichnung" value={title} onChange={setTitle} />
          <TextField label="Werkstatt" value={workshop} onChange={setWorkshop} />
          <DateField label="Datum" value={repairDate} onChange={setRepairDate} />
          <TextField label="Kosten (€)" value={cost} onChange={setCost} kind="money" />
          <SelectField
            label="Status"
            value={status}
            onChange={(v) => setStatus(v as RepairStatus)}
            options={REPAIR_STATUS_OPTIONS}
          />
          {localError || error ? <p className="text-sm text-rose-600">{localError || error}</p> : null}
        </div>
        <Footer
          onClose={onClose}
          saving={saving}
          label="Speichern"
          onSubmit={() => {
            const validation = validateRepairForm({ title, cost });
            if (validation) {
              setLocalError(validation);
              return;
            }
            onSubmit({
              title,
              workshop,
              repair_date: repairDate || null,
              cost: cost || null,
              status,
            });
          }}
        />
      </div>
    </AppModal>
  );
}

export function FuelCardFormModal({
  open,
  initial,
  saving,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean;
  initial?: VehicleFuelCard | null;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => void;
}) {
  const [provider, setProvider] = React.useState<FuelProvider>('dkv');
  const [lastFour, setLastFour] = React.useState('');
  const [limit, setLimit] = React.useState('');
  const [validUntil, setValidUntil] = React.useState('');
  const [formKey, setFormKey] = React.useState(0);

  React.useEffect(() => {
    if (!open) return;
    setProvider(initial?.provider ?? 'dkv');
    setLastFour(initial?.last_four ?? '');
    setLimit(initial ? String(initial.monthly_limit_cents / 100) : '');
    setValidUntil(initial?.valid_until ?? '');
    setFormKey((k) => k + 1);
  }, [open, initial]);
  const dirty = useDirtySnapshot(JSON.stringify([provider, lastFour, limit, validUntil]), formKey);

  return (
    <AppModal
      open={open}
      onClose={onClose}
      panelClassName="md:max-w-lg"
      overlayClassName="z-[70]"
      dirty={open && dirty}
      disableClose={saving}
    >
      <div className="flex h-full flex-col bg-app-surface">
        <div className="border-b border-app-border px-4 py-3">
          <h2 className="text-base font-semibold text-app-text">
            {initial ? 'Tankkarte ändern' : 'Tankkarte zuweisen'}
          </h2>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          <SelectField
            label="Anbieter"
            value={provider}
            onChange={(v) => setProvider(v as FuelProvider)}
            options={FUEL_PROVIDER_OPTIONS.filter((o) => o.value !== 'none')}
          />
          <TextField label="Kartennummer (letzte 4 Stellen)" value={lastFour} onChange={setLastFour} kind="last4" />
          <TextField label="Tanklimit (€ / Monat)" value={limit} onChange={setLimit} kind="money" />
          <DateField label="Gültig bis" value={validUntil} onChange={setValidUntil} />
          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        </div>
        <Footer
          onClose={onClose}
          saving={saving}
          label="Speichern"
          onSubmit={() =>
            onSubmit({
              provider,
              last_four: lastFour,
              monthly_limit: limit || 0,
              valid_until: validUntil || null,
            })
          }
        />
      </div>
    </AppModal>
  );
}

export function FuelTxFormModal({
  open,
  initial,
  saving,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean;
  initial?: VehicleFuelTransaction | null;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => void;
}) {
  const [occurredOn, setOccurredOn] = React.useState('');
  const [station, setStation] = React.useState('');
  const [liters, setLiters] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [formKey, setFormKey] = React.useState(0);

  React.useEffect(() => {
    if (!open) return;
    setOccurredOn(initial?.occurred_on ?? '');
    setStation(initial?.station ?? '');
    setLiters(litersToInput(initial?.liters_milli));
    setAmount(centsToEuroInput(initial?.amount_cents));
    setFormKey((k) => k + 1);
  }, [open, initial]);
  const dirty = useDirtySnapshot(JSON.stringify([occurredOn, station, liters, amount]), formKey);

  return (
    <AppModal
      open={open}
      onClose={onClose}
      panelClassName="md:max-w-lg"
      overlayClassName="z-[70]"
      dirty={open && dirty}
      disableClose={saving}
    >
      <div className="flex h-full flex-col bg-app-surface">
        <div className="border-b border-app-border px-4 py-3">
          <h2 className="text-base font-semibold text-app-text">
            {initial ? 'Tankvorgang bearbeiten' : 'Tankvorgang erfassen'}
          </h2>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          <DateField label="Datum" value={occurredOn} onChange={setOccurredOn} />
          <TextField label="Tankstelle" value={station} onChange={setStation} />
          <TextField label="Menge (Liter)" value={liters} onChange={setLiters} kind="liters" />
          <TextField label="Betrag (€)" value={amount} onChange={setAmount} kind="money" />
          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        </div>
        <Footer
          onClose={onClose}
          saving={saving}
          label="Speichern"
          onSubmit={() =>
            onSubmit({
              occurred_on: occurredOn,
              station,
              liters: liters || null,
              amount: amount || 0,
            })
          }
        />
      </div>
    </AppModal>
  );
}

export function DocumentUploadModal({
  open,
  saving,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (file: File, category: DocumentCategory, name: string) => void;
}) {
  const [file, setFile] = React.useState<File | null>(null);
  const [category, setCategory] = React.useState<DocumentCategory>('document');
  const [name, setName] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [formKey, setFormKey] = React.useState(0);

  React.useEffect(() => {
    if (!open) return;
    setFile(null);
    setCategory('document');
    setName('');
    setFormKey((k) => k + 1);
  }, [open]);
  const dirty = useDirtySnapshot(JSON.stringify([file?.name ?? null, category, name]), formKey);

  return (
    <AppModal
      open={open}
      onClose={onClose}
      panelClassName="md:max-w-lg md:!rounded-xl md:overflow-hidden"
      overlayClassName="z-[70]"
      dirty={open && dirty}
      disableClose={saving}
    >
      <div className="flex h-full flex-col overflow-hidden bg-app-surface md:rounded-xl">
        <div className="border-b border-app-border px-4 py-3">
          <h2 className="text-base font-semibold text-app-text">Dokument hochladen</h2>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          <button
            type="button"
            className="flex w-full flex-col items-center justify-center rounded-md border border-dashed border-app-border bg-app-bg px-4 py-8 text-sm text-app-text-muted hover:bg-app-surface"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const next = e.dataTransfer.files?.[0] ?? null;
              if (!next) return;
              setFile(next);
              if (!name) setName(next.name);
            }}
          >
            <span className="font-medium text-app-text">
              {file ? file.name : 'Datei hierher ziehen oder durchsuchen'}
            </span>
            <span className="mt-1 text-xs text-app-text-subtle">PDF, JPG, PNG · max. 20 MB</span>
          </button>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
            onChange={(e) => {
              const next = e.target.files?.[0] ?? null;
              setFile(next);
              if (next && !name) setName(next.name);
            }}
          />
          <Field label="Dateiname">
            <input className={SETTINGS_INPUT_CLASS} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <SelectField
            label="Dokumenttyp"
            value={category}
            onChange={(v) => setCategory(v as DocumentCategory)}
            options={DOCUMENT_CATEGORY_OPTIONS}
          />
          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        </div>
        <Footer
          onClose={onClose}
          saving={saving}
          label="Hochladen"
          onSubmit={() => {
            if (file) onSubmit(file, category, name);
          }}
        />
      </div>
    </AppModal>
  );
}

export function MaintenanceCompleteModal({
  open,
  reminder,
  vehicleMileage,
  saving,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean;
  reminder: VehicleReminder | null;
  vehicleMileage: number;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (payload: {
    performed_on: string;
    mileage: string;
    workshop: string;
    cost: string;
    note: string;
    file: File | null;
  }) => void;
}) {
  const [performedOn, setPerformedOn] = React.useState('');
  const [mileage, setMileage] = React.useState('');
  const [workshop, setWorkshop] = React.useState('');
  const [cost, setCost] = React.useState('');
  const [note, setNote] = React.useState('');
  const [file, setFile] = React.useState<File | null>(null);
  const [localError, setLocalError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [formKey, setFormKey] = React.useState(0);

  React.useEffect(() => {
    if (!open) return;
    const today = new Date();
    const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setPerformedOn(ymd);
    setMileage(vehicleMileage > 0 ? String(vehicleMileage) : '');
    setWorkshop('');
    setCost('');
    setNote('');
    setFile(null);
    setLocalError(null);
    setFormKey((k) => k + 1);
  }, [open, reminder, vehicleMileage]);
  const dirty = useDirtySnapshot(
    JSON.stringify([performedOn, mileage, workshop, cost, note.trim(), file?.name ?? null]),
    formKey,
  );

  return (
    <AppModal
      open={open}
      onClose={onClose}
      panelClassName="md:max-w-lg md:!rounded-xl md:overflow-hidden"
      overlayClassName="z-[70]"
      dirty={open && dirty}
      disableClose={saving}
    >
      <div className="flex h-full flex-col overflow-hidden bg-app-surface md:rounded-xl">
        <div className="border-b border-app-border px-4 py-3">
          <h2 className="text-base font-semibold text-app-text">Wartung abschließen</h2>
          {reminder ? <p className="mt-0.5 text-sm text-app-text-subtle">{reminder.display_label}</p> : null}
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          <DateField label="Durchgeführt am" value={performedOn} onChange={setPerformedOn} required />
          <TextField label="km-Stand" value={mileage} onChange={setMileage} kind="int" />
          <TextField label="Werkstatt" value={workshop} onChange={setWorkshop} />
          <TextField label="Kosten (€)" value={cost} onChange={setCost} kind="money" />
          <TextField label="Notiz" value={note} onChange={setNote} />
          <button
            type="button"
            className="flex w-full flex-col items-center justify-center rounded-md border border-dashed border-app-border bg-app-bg px-4 py-6 text-sm text-app-text-muted hover:bg-app-surface"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              setFile(e.dataTransfer.files?.[0] ?? null);
            }}
          >
            <span className="font-medium text-app-text">
              {file ? file.name : 'Beleg optional hierher ziehen oder durchsuchen'}
            </span>
            <span className="mt-1 text-xs text-app-text-subtle">PDF, JPG, PNG · max. 20 MB</span>
          </button>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {localError || error ? <p className="text-sm text-rose-600">{localError || error}</p> : null}
        </div>
        <Footer
          onClose={onClose}
          saving={saving}
          label="Abschließen"
          onSubmit={() => {
            const validation = validateCompleteForm({ performedOn, mileage, cost });
            if (validation) {
              setLocalError(validation);
              return;
            }
            if (file && file.size > 20 * 1024 * 1024) {
              setLocalError('Datei ist größer als 20 MB.');
              return;
            }
            onSubmit({
              performed_on: performedOn,
              mileage,
              workshop,
              cost,
              note,
              file,
            });
          }}
        />
      </div>
    </AppModal>
  );
}
